import React, { ChangeEvent, useEffect, useRef, useState } from 'react'
import styled from '@emotion/styled'
import { Alert, Button, Input, Modal, Radio, Space, Typography } from 'antd'
import { DeleteOutlined, DownloadOutlined, UploadOutlined } from '@ant-design/icons'
import {
  createPortalArticleJsonTemplateExport,
  getUtf8ByteLength,
  parsePortalArticleJson,
  PORTAL_ARTICLE_JSON_MAX_BYTES,
  PortalArticleJsonImportResult,
  PortalArticleJsonTransferError,
} from '../helpers/portal-article-json-transfer'
import { downloadPortalArticleJson } from '../helpers/portal-article-json-download'

const { Paragraph, Text } = Typography
const { TextArea } = Input

type ImportSource = 'file' | 'paste'

interface PortalArticleJsonImportModalProps {
  open: boolean
  onCancel: () => void
  onImport: (result: PortalArticleJsonImportResult) => void
}

export function PortalArticleJsonImportModal({
  open,
  onCancel,
  onImport,
}: PortalArticleJsonImportModalProps) {
  const [source, setSource] = useState<ImportSource>('file')
  const [fileName, setFileName] = useState('')
  const [fileText, setFileText] = useState('')
  const [pastedText, setPastedText] = useState('')
  const [readingFile, setReadingFile] = useState(false)
  const [error, setError] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (open) return
    setSource('file')
    setFileName('')
    setFileText('')
    setPastedText('')
    setReadingFile(false)
    setError('')
  }, [open])

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0]
    event.currentTarget.value = ''
    if (!file) return

    setError('')
    setSource('file')
    setFileName('')
    setFileText('')

    const hasJsonExtension = file.name.toLowerCase().endsWith('.json')
    if (!hasJsonExtension) {
      setError('Selecione um arquivo JSON com extensão .json.')
      return
    }
    if (file.size > PORTAL_ARTICLE_JSON_MAX_BYTES) {
      setError('O JSON deve possuir no máximo 2 MB.')
      return
    }

    setReadingFile(true)
    try {
      const text = await file.text()
      if (getUtf8ByteLength(text) > PORTAL_ARTICLE_JSON_MAX_BYTES) {
        setError('O JSON deve possuir no máximo 2 MB.')
        return
      }
      setFileName(file.name)
      setFileText(text)
    } catch {
      setError('Não foi possível ler o arquivo. Selecione-o novamente.')
    } finally {
      setReadingFile(false)
    }
  }

  const clearFile = () => {
    setFileName('')
    setFileText('')
    setError('')
  }

  const handleImport = () => {
    const text = source === 'file' ? fileText : pastedText
    try {
      const result = parsePortalArticleJson(text)
      setError('')
      onImport(result)
    } catch (importError) {
      setError(formatImportError(importError))
    }
  }

  const handleDownloadTemplate = () => {
    try {
      const template = createPortalArticleJsonTemplateExport()
      downloadPortalArticleJson(template.text, template.fileName)
      setError('')
    } catch {
      setError('Não foi possível baixar o modelo JSON. Tente novamente.')
    }
  }

  const activeText = source === 'file' ? fileText : pastedText

  return (
    <Modal
      title="Importar artigo em JSON"
      open={open}
      okText="Importar no rascunho"
      cancelText="Cancelar"
      confirmLoading={readingFile}
      okButtonProps={{ disabled: readingFile || !activeText.trim() }}
      onOk={handleImport}
      onCancel={onCancel}
      width={720}
    >
      <Paragraph>
        O JSON preencherá este novo artigo como rascunho. Nada será salvo e nenhuma
        imagem será enviada automaticamente.
      </Paragraph>

      <TemplateHelp>
        <Text type="secondary">
          Precisa de uma referência? Baixe um exemplo básico e substitua os textos,
          slug e referências de imagem.
        </Text>
        <Button icon={<DownloadOutlined />} onClick={handleDownloadTemplate}>
          Baixar modelo JSON
        </Button>
      </TemplateHelp>

      <SourceSelector>
        <Radio.Group
          value={source}
          optionType="button"
          buttonStyle="solid"
          onChange={(event) => {
            setSource(event.target.value)
            setError('')
          }}
          options={[
            { value: 'file', label: 'Selecionar arquivo' },
            { value: 'paste', label: 'Colar JSON' },
          ]}
        />
      </SourceSelector>

      {source === 'file' ? (
        <FilePanel>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json,application/json"
            hidden
            aria-label="Selecionar JSON do artigo"
            onChange={handleFileChange}
          />
          <Space wrap>
            <Button
              icon={<UploadOutlined />}
              loading={readingFile}
              onClick={() => fileInputRef.current?.click()}
            >
              Selecionar JSON
            </Button>
            {fileName && (
              <Button icon={<DeleteOutlined />} onClick={clearFile}>
                Limpar
              </Button>
            )}
          </Space>
          {fileName ? (
            <Text>
              Arquivo ativo: <strong>{fileName}</strong>
            </Text>
          ) : (
            <Text type="secondary">Nenhum arquivo selecionado.</Text>
          )}
        </FilePanel>
      ) : (
        <TextArea
          value={pastedText}
          placeholder={'Cole aqui o conteúdo com { "versao": 1, "artigo": { ... } }'}
          autoSize={{ minRows: 10, maxRows: 20 }}
          onChange={(event) => {
            setPastedText(event.target.value)
            setError('')
          }}
        />
      )}

      <LimitText type="secondary">Limite: 2 MB. Somente um artigo por importação.</LimitText>

      {error && <Alert type="error" showIcon message="Não foi possível importar" description={error} />}
    </Modal>
  )
}

function formatImportError(error: unknown) {
  if (error instanceof PortalArticleJsonTransferError) {
    return error.path ? `${error.message} Campo: ${error.path}` : error.message
  }
  return error instanceof Error ? error.message : 'Revise o JSON e tente novamente.'
}

const SourceSelector = styled.div`
  margin-bottom: 16px;
`

const TemplateHelp = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;

  @media (max-width: 600px) {
    align-items: flex-start;
    flex-direction: column;
  }
`

const FilePanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  min-height: 120px;
  padding: 20px;
  border: 1px dashed #d9d9d9;
  border-radius: 6px;
  background: #fafafa;
`

const LimitText = styled(Text)`
  display: block;
  margin: 12px 0;
`
